import {
    FindPeopleJsonResponse,
    FindUnifiedGroupsJsonResponse,
    getServiceAsync,
} from '../../service';
import { getJsonRequestHeader } from 'owa-service/lib/ServiceRequestUtils';
import {
    runOwsRequestWithMonitor,
    HubPersona,
    createHubPersona,
    Feature,
    fullResponseOperationOption,
    UnifiedGroup,
    convertToUnifiedGroup,
    PersonType,
    allPeopleFolderId,
    allListsFolderId,
} from '../../shared';
import { QueryData, QuerySource } from '../models/query';
import { getFindPeopleRequest } from './common/baseFindPeopleRequest';

const unifiedGroupsPageSize: number = 100;

function searchContacts(queryData: QueryData): Promise<HubPersona[]> {
    return getServiceAsync().then(service => {
        const body = getFindPeopleRequest(
            service.Factory,
            queryData,
            queryData.source === QuerySource.Directory
        );
        return runOwsRequestWithMonitor({
            scenarioName: 'GetSearchedPersonasContacts',
            feature: Feature.Search,
            operation: () =>
                service.Operation.findPeopleOperation(
                    service.Factory.findPeopleJsonRequest({
                        Header: getJsonRequestHeader(),
                        Body: service.Factory.findPeopleRequest(body),
                    }),
                    fullResponseOperationOption
                ),
            transformResponse: (owsResponse: FindPeopleJsonResponse) => {
                const hubPersonas = (owsResponse.Body.ResultSet.map(
                    createHubPersona
                ) as HubPersona[])
                    // Subtract ε < 1 (index / count) to tie-break equal relevancy with the order returned
                    .map((result, index) => ({
                        ...result,
                        relevancyScore:
                            (result.relevancyScore || 0) -
                            index / owsResponse.Body.ResultSet.length,
                    }));

                return hubPersonas.filter(persona => {
                    if (queryData.folderId === allPeopleFolderId) {
                        return persona.type === PersonType.Person;
                    }

                    if (queryData.source === QuerySource.Directory) {
                        if (queryData.folderId === allListsFolderId) {
                            return (
                                persona.type === PersonType.PublicDistributionList ||
                                persona.type === PersonType.PrivateDistributionList
                            );
                        } else {
                            return persona.type === PersonType.Person;
                        }
                    }

                    return true;
                });
            },
        });
    });
}

function searchUnifiedGroups(queryData: QueryData): Promise<HubPersona[]> {
    return getServiceAsync().then(service =>
        runOwsRequestWithMonitor({
            scenarioName: 'GetSearchedUnifiedGroups',
            feature: Feature.Search,
            operation: () =>
                service.Operation.findUnifiedGroupsOperation(
                    service.Factory.findUnifiedGroupsJsonRequest({
                        Header: getJsonRequestHeader(),
                        Body: service.Factory.findUnifiedGroupsRequest({
                            QueryString: queryData.currentQueryString,
                            IncludeUserRelationship: true,
                            PageSize: unifiedGroupsPageSize,
                        }),
                    }),
                    fullResponseOperationOption
                ),
            transformResponse: (owsResponse: FindUnifiedGroupsJsonResponse) => {
                let returnData: UnifiedGroup[] = [];
                (owsResponse.Body.GroupsSets || []).forEach(groupSet => {
                    returnData = returnData.concat(
                        (groupSet.Groups || []).map(group => convertToUnifiedGroup(group, false))
                    );
                });

                return returnData;
            },
        })
    );
}

export function getSearchUserFolders(queryData: QueryData): Promise<HubPersona[]> {
    return searchContacts(queryData);
}

export function getSearchUnifiedGroups(queryData: QueryData): Promise<HubPersona[]> {
    return searchUnifiedGroups(queryData);
}
